'use client';

import { useAtomValue } from 'jotai';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';

import { PLP_FILTERS_STATE } from '@cookies/Constants/Cookie.constants';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import type { PageType } from '@core/Constants/PageType';
import { PAGE_TYPE } from '@core/Constants/PageType';
import type { FilterKey, FilterParams } from '@core/Entities/Search/QueryParams.entity';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import type { Make } from '@core/Entities/Vehicle/Make.entity';
import type { Model } from '@core/Entities/Vehicle/Model.entity';
import { getModelNameFromId } from '@core/Services/Search/Utils/GetModelNameFromId';
import { safeJsonParse } from '@core/Utils/Json/SafeJsonParse';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import Button from '@gds/Button/Button';
import type { ComponentProps } from '@gds/Button/Button.types';
import { ChevronLeft } from '@gds/Icons/Paths/ChevronLeft';
import { generateBreadcrumbClickedTracking } from '@tracking/BreadcrumbClicked.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import styles from './BackToPlp.module.css';

interface BackToPlpProps extends Omit<ComponentProps, 'children'> {
  buttonText: string;
  className?: string;
  pageType: PageType;
  make?: Make;
  model?: Model;
}

const BackToPlp: React.FC<BackToPlpProps> = ({
  buttonText,
  className = '',
  pageType,
  make,
  model,
  ...rest
}) => {
  const router = useRouter();
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const decodedValue = decodeURIComponent(Cookies.get(PLP_FILTERS_STATE) ?? '');

  const filterParams: FilterParams = safeJsonParse(decodedValue, {});
  const filterParamsArray: Filter[] = Object.entries(filterParams).map(([key, value]) => ({
    id: key as FilterKey,
    value: value !== undefined ? (Array.isArray(value) ? value : [value]) : undefined,
  }));
  const plpUrl = createPathFromFilters(filterParamsArray);

  const handleClick = () => {
    const modelSlug = model ? getModelNameFromId(model.id!) : undefined;

    if (pageType === PAGE_TYPE.Leasing && /\/leasing(\/|\?|$)/.test(document.referrer)) {
      router.back();
      return;
    }

    let url = `/cars/used`;

    if (pageType === PAGE_TYPE.Plp || pageType === PAGE_TYPE.CarCompare) {
      url = plpUrl;
    } else if (pageType === PAGE_TYPE.LeasingPlp) {
      url = `/cars/lease-deals`;
    } else if (pageType === PAGE_TYPE.Leasing) {
      url = `/leasing`;
    } else if (make && modelSlug) {
      url = `/cars/used/${make.id}/${modelSlug}`;
    }
    const { event, context } = generateBreadcrumbClickedTracking({
      searchParams,
      userId,
      formState,
      ctaWording: buttonText,
      ctaUrl: url,
      clickPosition: 'pdp',
      ctaPosition: 1,
    });

    trackCustomEvent({ event, context });
    router.push(url);
  };

  return (
    <Button
      variant="link"
      Component="a"
      className={`${styles.backButton} ${className}`}
      leftIcon={<ChevronLeft />}
      onClick={handleClick}
      {...rest}
    >
      {buttonText}
    </Button>
  );
};

export { BackToPlp };
